import { getEnvName, isSSR } from '@cvs-web/splash-components/server';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '../constants/envConstants';
import {
  ContactPoint,
  CommPrefChannel,
  RxNotificationType,
  ECPhoneNumberObj,
  PreferencesProgramObj,
  ChannelObjSystem,
} from '../types/datatypes';
import { PHONE_TYPE_USE, SPLTY_PROGRAMS } from '@/lib/constants/stringConstants/communicationPreferences';

const getCurrentEnv = (): EnvironmentConfig => {
  const envName = getEnvName();

  return ENVIRONMENT_CONFIG[envName];
};

export const environment: EnvironmentConfig = getCurrentEnv();

export const getDeviceType = (): string => {
  // Other
  let deviceType: string = 'DESKTOP';
  if (isSSR()) return deviceType;
  const ua: string = window?.navigator?.userAgent;

  // Android Specific Checks
  if (/Android/i.test(ua)) {
    // Android Mobile
    if (/Mobile/i.test(ua)) {
      deviceType = 'AND_MOBILE';
    } else if (/Glass/i.test(ua)) {
      // Android Glass
      deviceType = 'AND_GLASS';
    } else {
      // Android Tablet
      deviceType = 'AND_TABLET';
    }
  } else if (/iPhone|iPod/i.test(ua)) {
    // iOS Mobile
    deviceType = 'IOS_MOBILE';
  } else if (/iPad/i.test(ua)) {
    // iOS Tablet
    deviceType = 'IOS_TABLET';
  } else if (/IEMobile/i.test(ua)) {
    // Windows
    deviceType = 'WIN_MOBILE';
  } else if (/webOS|BlackBerry|Opera Mini/i.test(ua)) {
    // Other Identified Vendor
    deviceType = 'OTH_MOBILE';
  }
  return deviceType;
};

export const isHealth100Client = (): boolean => {
  if (isSSR()) return false;
  const ua: string = window?.navigator?.userAgent;
  return /Health100/i.test(ua);
};

export const formatToPhone = (event: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>) => {
  if (event.type !== 'elementBlur' && event.nativeEvent instanceof KeyboardEvent && isModifierKey(event.nativeEvent)) {
    return;
  }

  const target = event.target as HTMLInputElement;
  const input = target.value.replace(/\D/g, '').substring(0, 10); // First ten digits of input only
  const areaCode = input.substring(0, 3);
  const middle = input.substring(3, 6);
  const last = input.substring(6, 10);

  if (input.length > 6) {
    target.value = `${areaCode}-${middle}-${last}`;
  } else if (input.length > 3) {
    target.value = `${areaCode}-${middle}`;
  } else if (input.length > 0) {
    target.value = `${areaCode}`;
  }
};

const isNumericInput = (event: KeyboardEvent) => {
  const key = event.keyCode;
  return (
    (key >= 48 && key <= 57) || // Allow number line
    (key >= 96 && key <= 105) // Allow number pad
  );
};

const isModifierKey = (event: KeyboardEvent) => {
  const key = event.keyCode;
  return (
    event.shiftKey ||
    key === 35 ||
    key === 36 || // Allow Shift, Home, End
    key === 8 ||
    key === 9 ||
    key === 13 ||
    key === 46 || // Allow Backspace, Tab, Enter, Delete
    (key > 36 && key < 41) || // Allow left, up, right, down
    // Allow Ctrl/Command + A,C,V,X,Z
    ((event.ctrlKey || event.metaKey) &&
      (key === 65 || key === 67 || key === 86 || key === 88 || key === 90))
  );
};

export const enforceNumber = (event: React.KeyboardEvent<HTMLInputElement>) => {
  // Input must be of a valid number format or a modifier key, and not longer than ten digits
  if (!isNumericInput(event.nativeEvent) && !isModifierKey(event.nativeEvent)) {
    event.preventDefault();
  }
};

export const getUnformattedPhoneNumber = (phoneNumber: string) => {
  return phoneNumber.replace(/[^0-9]/g, '');
};

export const getFormattedNumber = (phoneNumber: string) => {
  if (!phoneNumber) return '';
  const unformattedNumber = phoneNumber.replace(/\D/g, '');
  return `${unformattedNumber.slice(0, 3)}-${unformattedNumber.slice(3, 6)}-${unformattedNumber.slice(6)}`;
};

export const getShowResolutionModalForText = (contactPoints: ContactPoint[], newPhoneNumber: string) => {
  const textNumber = contactPoints?.find((obj) => obj.system === 'SMS')?.value;
  const callPhoneType = contactPoints?.find((obj) => obj.system === 'PHONE')?.use;
  return textNumber && getUnformattedPhoneNumber(textNumber) !== getUnformattedPhoneNumber(newPhoneNumber)
    ? callPhoneType === 'Mobile'
    : false;
};

export const getRxPrefCurrentValues = (prefChannel?: CommPrefChannel, prefContactPoint?: ContactPoint) => {
  const prefNumber = prefContactPoint?.value || '';
  const prefType = (prefContactPoint?.use && PHONE_TYPE_USE[prefContactPoint?.use]) || PHONE_TYPE_USE.Mobile;
  let commPrefValue = '';
  if (prefChannel?.subscriptionStatus === 'OUT') {
    commPrefValue = RxNotificationType.NONE;
  } else if (
    prefChannel?.subscriptionStatus === 'IN' &&
    prefChannel?.tcpa?.type === 'Marketing' &&
    prefChannel?.tcpa?.code === 'Y'
  ) {
    commPrefValue = RxNotificationType.PRESCRIPTION_AND_MARKETING;
  } else if (
    prefChannel?.subscriptionStatus === 'IN' &&
    prefChannel?.tcpa?.type === 'Marketing' &&
    prefChannel?.tcpa?.code === 'D'
  ) {
    commPrefValue = RxNotificationType.PRESCRIPTION_ONLY;
  }
  return { prefNumber, commPrefValue, prefType };
};

export const ecTextNumberNACheck = (textECPhoneObj: ECPhoneNumberObj) => {
  return (
    textECPhoneObj?.phone_area_cd_nbr !== 'n/a' &&
    textECPhoneObj?.phone_pfx_nbr !== 'n/a' &&
    textECPhoneObj?.phone_sfx_nbr !== 'n/a'
  );
};

export const getNormalizedECTextNumber = (ecPhoneNumbersArray: ECPhoneNumberObj[]) => {
  const textECPhoneObj = ecPhoneNumbersArray?.find((obj) => obj.phone_type_cd === 'S');
  let textECPhone;
  if (!textECPhoneObj) {
    textECPhone = '';
  } else if (
    ecTextNumberNACheck(textECPhoneObj) &&
    textECPhoneObj?.phone_area_cd_nbr &&
    textECPhoneObj?.phone_pfx_nbr &&
    textECPhoneObj?.phone_sfx_nbr
  ) {
    textECPhone = `${textECPhoneObj?.phone_area_cd_nbr}-${textECPhoneObj?.phone_pfx_nbr}-${textECPhoneObj?.phone_sfx_nbr}`;
  }

  return textECPhone;
};

export const getSpecialtyCurrentPref = (programsArray: PreferencesProgramObj[] | undefined, system: string) => {
  if (!programsArray || programsArray.length === 0) return '';

  let allIn = false;
  let allOut = false;

  programsArray.forEach((program) => {
    if (!program?.channels) return;

    program?.channels?.forEach((channel) => {
      if (channel.system !== system.toUpperCase()) return;

      if (channel.subscriptionStatus === 'IN') {
        allIn = true;
      } else if (channel.subscriptionStatus === 'OUT') {
        allOut = true;
      }

      if (allIn && allOut) return;
    });
  });

  if (!allIn && !allOut) return '';
  if (allIn && !allOut) return 'IN';
  if (allOut && !allIn) return 'OUT';

  return '';
};

export const getSpltyUpdatedPrograms = (updatedSystem: ChannelObjSystem, newSubscriptionStatus: 'IN' | 'OUT') => {
  return SPLTY_PROGRAMS.map((program) => {
    const updatedChannels = [
      { system: updatedSystem, subsystem: null, subscriptionStatus: newSubscriptionStatus },
      { system: updatedSystem, subsystem: 'OLP', subscriptionStatus: newSubscriptionStatus },
    ];
    return {
      name: program,
      description: 'Specialty Rx Preferences',
      channels: updatedChannels,
    };
  });
};

export const getReturnUrl = () => {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const returnUrl = params?.get('returnUrl');
    // Validate returnUrl to prevent open redirect
    if (returnUrl) {
      try {
        const url = new URL(returnUrl, window.location.origin);
        // Only allow same-origin URLs
        if (url.origin === window.location.origin) {
          return returnUrl;
        }
      } catch {
        // Invalid URL, ignore
      }
    }
  }
};

export const routeOrReturn = (
  callback: () => void,
  returnParams?: {
    [key: string]: string;
  },
) => {
  const returnUrl = getReturnUrl();
  if (!!returnUrl && window !== undefined) {
    const searchParams = new URLSearchParams(returnParams || {});
    const returnSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';
    window.location.href = `${returnUrl}${returnSearch}`;
    return;
  }
  callback();
};

export const getCaremarkPrefs = (system: string, programs: PreferencesProgramObj[]) => {
  let enrolledStatus: string | undefined = '';
  const initialPrefs: [string[], PreferencesProgramObj[]] = [[], []];
  const [selectedOptions, availablePrograms] = (programs || []).reduce((acc: [string[], PreferencesProgramObj[]], program) => {
    const programChannel = program.channels.find((channel) => channel.system === system);
    if (programChannel && programChannel.visible && program.programID !== '101') {
      acc[1].push(program); // add visible programs to available options
      if (programChannel.subscriptionStatus === 'IN') {
        enrolledStatus = 'IN';
        acc[0].push(program.programID); // add selected options only if enrolled
      } else if (!enrolledStatus && programChannel.subscriptionStatus === 'OUT') {
        enrolledStatus = 'OUT';
      }
    }
    return acc;
  }, initialPrefs);

  return [selectedOptions, availablePrograms, enrolledStatus] as const;
};

export const formatCaremarkProgramTagString = (
  values: (string | undefined)[] | undefined,
  programs: PreferencesProgramObj[],
) => {
  const selectedPrograms = values
    ?.map((optionID) => {
      return programs
        .find((program) => {
          return program.programID == optionID;
        })
        ?.name.toLowerCase()
        .replace(/ /g, '_');
    })
    .join('|');
  return `${selectedPrograms}`;
};

export const caremarkEnrollTag = (enrollCmkOption: string) => {
  return enrollCmkOption === 'IN' ? 'enroll' : 'dont_enroll';
};
